import {useState, useSyncExternalStore} from "react";
import {Link} from "react-router-dom";
import Button from "./Button";
import {
    applyConsent,
    type ConsentCategory,
    hasDecided,
    isMarketingTagRequested,
    subscribeMarketingTagRequested,
    writeConsent,
} from "../analytics/consent";

const MEASUREMENT_ID = import.meta.env.VITE_GA_ID;

const DESCRIPTIONS: Record<ConsentCategory, {label: string; text: string}> = {
    analytics: {
        label: "Analytics",
        text: "cookies that help us understand how visitors use our site",
    },
    marketing: {
        label: "Advertising",
        text: "the event organiser's Meta (Facebook and Instagram) pixel, which tells them whether their adverts led you here and lets Meta show you related adverts",
    },
};

export default function CookieConsentBanner() {
    // A category is only asked about when one of its tags is actually configured: GA from the build,
    // host pixels from the page being viewed. Each is asked once, so a visitor who answered the
    // analytics-only banner is asked again about advertising alone.
    const marketingRequested = useSyncExternalStore(
        subscribeMarketingTagRequested,
        isMarketingTagRequested,
    );
    const [answered, setAnswered] = useState<ConsentCategory[]>([]);
    const [selected, setSelected] = useState<Record<ConsentCategory, boolean>>({
        analytics: false,
        marketing: false,
    });

    const pending: ConsentCategory[] = [];
    if (Boolean(MEASUREMENT_ID) && !hasDecided("analytics")) pending.push("analytics");
    if (marketingRequested && !hasDecided("marketing")) pending.push("marketing");
    const open = pending.filter((c) => !answered.includes(c));

    if (open.length === 0) return null;

    function decide(choices: Partial<Record<ConsentCategory, boolean>>) {
        applyConsent(writeConsent(choices));
        setAnswered((prev) => [...prev, ...open]);
    }

    function decideAll(granted: boolean) {
        decide(Object.fromEntries(open.map((c) => [c, granted])));
    }

    const single = open.length === 1 ? DESCRIPTIONS[open[0]] : null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4"
        >
            <div className="mx-auto max-w-3xl rounded-t-xl border border-gray-200 bg-white p-5 shadow-lg sm:rounded-xl">
                <p className="text-sm text-gray-600">
                    {single
                        ? `We would like to use ${single.text}. You can accept or decline this.`
                        : "We would like your permission for each of the following. You can accept or decline them separately."}{" "}
                    See our{" "}
                    <Link
                        to="/legal/privacy-policy#cookies"
                        className="font-medium text-orange-700 underline hover:text-orange-800"
                    >
                        Privacy Policy
                    </Link>
                    .
                </p>

                {!single && (
                    <fieldset className="mt-3 space-y-2">
                        <legend className="sr-only">Cookie categories</legend>
                        {open.map((category) => (
                            <label key={category} className="flex items-start gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                    checked={selected[category]}
                                    onChange={(e) =>
                                        setSelected((prev) => ({...prev, [category]: e.target.checked}))
                                    }
                                />
                                <span>
                                    <span className="font-medium text-gray-900">
                                        {DESCRIPTIONS[category].label}:
                                    </span>{" "}
                                    {DESCRIPTIONS[category].text}.
                                </span>
                            </label>
                        ))}
                    </fieldset>
                )}

                <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => decideAll(false)}>
                        {single ? "Decline" : "Decline all"}
                    </Button>
                    {!single && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                decide(Object.fromEntries(open.map((c) => [c, selected[c]])))
                            }
                        >
                            Save choices
                        </Button>
                    )}
                    <Button variant="primary" size="sm" onClick={() => decideAll(true)}>
                        {single ? "Accept" : "Accept all"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
